/**
 * i18next initialization (I18N_PLAN.md, task 0.1).
 *
 * Namespace layout: one JSON file per component directory under
 * locales/en/ (see I18N_PLAN.md, Architecture decision 2). Keys follow
 * `<namespace>:<ComponentName>.<slug>` (decision 3).
 *
 * Locale resolution: localStorage first (set by the language switcher),
 * then the browser's navigator.language, falling back to English.
 *
 * en-XA is the generated pseudo-locale (npm run i18n:pseudo). It is
 * gitignored and loaded only in development — via webpack's
 * require.context on the always-present parent dir, so a missing en-XA/
 * can never break the build, jest (NODE_ENV=test) never evaluates it,
 * and production builds eliminate the branch entirely. After
 * (re)generating pseudo files, restart the dev server — webpack contexts
 * are built at compile time.
 */

import i18n, { Resource } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import careerTransition from "./locales/en/career-transition.json";
import coach from "./locales/en/coach.json";
import common from "./locales/en/common.json";
import home from "./locales/en/home.json";
import linkedin from "./locales/en/linkedin.json";
import outcome from "./locales/en/outcome.json";
import pagesAccount from "./locales/en/pages-account.json";
import pagesCore from "./locales/en/pages-core.json";
import pagesProducts from "./locales/en/pages-products.json";
import portfolioKit from "./locales/en/portfolio-kit.json";
import resume from "./locales/en/resume.json";
import roadmap from "./locales/en/roadmap.json";

// Spanish (I18N_PLAN.md Phase 5) — machine-drafted by scripts/translate-locales.js,
// human-spot-reviewed. Unlike en-XA (the dev-only pseudo-locale), these ARE
// real production resources, loaded unconditionally.
import careerTransitionEs from "./locales/es/career-transition.json";
import coachEs from "./locales/es/coach.json";
import commonEs from "./locales/es/common.json";
import homeEs from "./locales/es/home.json";
import linkedinEs from "./locales/es/linkedin.json";
import outcomeEs from "./locales/es/outcome.json";
import pagesAccountEs from "./locales/es/pages-account.json";
import pagesCoreEs from "./locales/es/pages-core.json";
import pagesProductsEs from "./locales/es/pages-products.json";
import portfolioKitEs from "./locales/es/portfolio-kit.json";
import resumeEs from "./locales/es/resume.json";
import roadmapEs from "./locales/es/roadmap.json";

export const NAMESPACES = [
  "common",
  "pages-core",
  "pages-products",
  "pages-account",
  "resume",
  "roadmap",
  "portfolio-kit",
  "home",
  "linkedin",
  "coach",
  "outcome",
  "career-transition",
] as const;

/** Dev-only pseudo-locale code (generated by scripts/gen-pseudo-locale.js). */
export const PSEUDO_LOCALE = "en-XA";

const resources: Resource = {
  en: {
    common,
    "pages-core": pagesCore,
    "pages-products": pagesProducts,
    "pages-account": pagesAccount,
    resume,
    roadmap,
    "portfolio-kit": portfolioKit,
    home,
    linkedin,
    coach,
    outcome,
    "career-transition": careerTransition,
  },
  es: {
    common: commonEs,
    "pages-core": pagesCoreEs,
    "pages-products": pagesProductsEs,
    "pages-account": pagesAccountEs,
    resume: resumeEs,
    roadmap: roadmapEs,
    "portfolio-kit": portfolioKitEs,
    home: homeEs,
    linkedin: linkedinEs,
    coach: coachEs,
    outcome: outcomeEs,
    "career-transition": careerTransitionEs,
  },
};

// Dev-only pseudo-locale (en-XA), loaded with Vite's import.meta.glob.
//
// This replaced webpack's require.context, which Vite has no equivalent of and
// which threw `ReferenceError: require is not defined` under `vite dev`.
//
// Two behavioural improvements over the webpack version:
//   * en-XA/ is GENERATED (npm run i18n:pseudo) and gitignored, so it is often
//     absent. require.context had to be pointed at the always-present parent
//     ./locales and filtered by regex, or it would throw on a missing dir.
//     A glob that matches nothing is simply an empty object — no workaround.
//   * The `import.meta.env.DEV` guard is statically replaced with `false` in a
//     production build, so Rollup drops this block AND the globbed JSON with
//     it. The pseudo-locale can never reach a production bundle.
//
// Still true from the webpack era: after (re)generating pseudo files you must
// restart the dev server, because the glob is resolved at transform time.
if (import.meta.env.DEV) {
  const pseudoModules = import.meta.glob<Record<string, unknown>>(
    "./locales/en-XA/*.json",
    { eager: true }
  );
  for (const [filePath, mod] of Object.entries(pseudoModules)) {
    // path shape: "./locales/en-XA/common.json" → ns "common"
    const ns = filePath.match(/\/([^/]+)\.json$/)?.[1];
    if (!ns) continue;
    resources[PSEUDO_LOCALE] = resources[PSEUDO_LOCALE] || {};
    (resources[PSEUDO_LOCALE] as Record<string, unknown>)[ns] =
      (mod as { default?: unknown }).default ?? mod;
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    interpolation: {
      escapeValue: false, // React already escapes rendered strings
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
    returnNull: false,
    // A missing key renders visibly as ??key?? instead of silently falling
    // back to English — lets the Phase 5 Playwright es run grep for gaps
    // rather than relying on a human to notice quietly-still-English text
    // (I18N_PLAN.md Phase 5 step 3).
    parseMissingKeyHandler: (key) => `??${key}??`,
  });

/**
 * Keep `<html lang>` in sync with the active language.
 *
 * i18next does not touch the document — applying the resolved language to it is
 * the app's job, and it had never been done. Found in production 2026-08-24: the
 * site rendered Spanish end to end while the document still said lang="en".
 * Screen readers take pronunciation from that attribute, so VoiceOver and NVDA
 * read Spanish copy with English phonetics; search engines index the page as
 * English.
 *
 * Applied once for the startup language AND on every change. The startup call is
 * the load-bearing half: a returning Spanish user resolves to `es` from
 * localStorage and never fires a change event, so a change-only listener would
 * leave exactly the people who need it stuck on lang="en".
 *
 * Lives here rather than in LanguageSwitcher because `changeLanguage` has two
 * call sites — the switcher and the login-time locale apply in useAuth — and a
 * component-level fix would silently miss the second.
 */
const applyDocumentLang = (lng: string): void => {
  if (typeof document === "undefined" || !lng) return;
  document.documentElement.lang = lng;
};

applyDocumentLang(i18n.resolvedLanguage ?? i18n.language ?? "en");
i18n.on("languageChanged", applyDocumentLang);

export default i18n;
